/**
 * Making the calendar fit the box we were given, whatever the box turns out to be.
 *
 * ---------------------------------------------------------------------------
 * WHY A PANEL HAS TO MEASURE ITSELF AT ALL
 * ---------------------------------------------------------------------------
 *
 * Because in an iframe nothing else will. The host pages frame us with
 * `scrolling="no"` and a `min-height`, and `scrolling="no"` is stronger than any
 * `overflow` the child sets on itself: the frame's viewport cannot scroll, full
 * stop. So a panel taller than the frame is not "reachable by scrolling" — it is
 * GONE, cut at the frame's edge with nothing on screen to say so. That is the
 * defect reported twice: "it was overflowing and not rendering the bottom" on
 * the camping page, worked around with `min-height: 1100px`, and then the same
 * thing on the rental page where the workaround was not available.
 *
 * Asking the host to grow is the right first move and already happens — the
 * `scroll` message. But it is a REQUEST. A plain `<iframe>` with no loader
 * beside it (`/kampeer-tarieven-2026` is exactly that) has nobody listening, and
 * a request nobody answers must not be the only plan.
 *
 * ---------------------------------------------------------------------------
 * SO: FIT, AND ASK ANYWAY
 * ---------------------------------------------------------------------------
 *
 * `maxHeight` is what we render at — never more than the room actually in front
 * of us, so the panel is whole and its month list scrolls inside itself rather
 * than being clipped by the frame.
 *
 * `wants` is what we ASK FOR — the height the panel would have taken if nothing
 * had stopped it. It is published on the element as `data-pw-grow` and read by
 * the compat bridge's height reporter, which is the one place that talks to the
 * host.
 *
 * The two together are SELF-CORRECTING, which is the whole point:
 *
 *   host listens      grows the frame -> `resize` fires in here -> more room ->
 *                     the cap lands above the natural height and does nothing.
 *                     No inner scrollbar, the desktop popover as designed.
 *   host ignores us   the cap holds, the months scroll inside the panel, and
 *                     every date stays reachable. No `min-height` to discover.
 *
 * Neither branch needs to know which one it is in, which is why there is no flag
 * and no timer here.
 */

/** Breathing room under the panel, so a shadow is not read as a cut edge. */
export const PANEL_GAP = 8;

/**
 * Never squeeze below this, even if the arithmetic says to.
 *
 * A field near the bottom of a short frame leaves almost nothing under it, and a
 * 40px calendar is not a calendar. Below this we overflow deliberately and lean
 * on `wants` instead — which the host usually does answer.
 */
export const PANEL_MIN = 240;

export interface PanelFit {
  /** What to render at. Apply only when the panel is taller than this. */
  maxHeight: number;
  /** How tall the DOCUMENT must be for the panel to be whole. */
  wants: number;
}

/**
 * @param top      the panel's top edge, relative to the viewport
 * @param natural  what the panel measures with no cap on it
 * @param viewport the visible height — inside a frame, the FRAME's height
 * @param scrollY  the document's scroll offset, so `wants` is in document space
 */
export function fitPanel(
  top: number,
  natural: number,
  viewport: number,
  scrollY = 0,
): PanelFit {
  return {
    maxHeight: Math.max(PANEL_MIN, Math.round(viewport - top - PANEL_GAP)),
    wants: Math.round(top + scrollY + natural + PANEL_GAP),
  };
}
